import { useEffect, useState } from "react";
import { FiSearch, FiUserPlus, FiX } from "react-icons/fi";
import { getApiUrl } from "../../../../config/api";
import {
  adminLimitSourceLabel,
  formatUsdWithCode,
  percentUsed,
} from "../../../../components/usageBilling/usageFormat";

const PAGE_SIZE = 20;

// The users who used AI in the selected cycle, highest spend first (the
// backend sorts). Only people with usage are listed, so the "any user" button
// is how an admin reaches someone who hasn't used AI yet. The current cycle
// also shows each user's week against its quota.
export default function UsageUsersTable({
  cycle,
  groups,
  query,
  onQueryChange,
  isCurrent,
  enforced,
  refreshKey,
  onSelectUser,
  onPickUser,
  canEdit,
  currentUser,
  language,
  formatMoney = formatUsdWithCode,
}) {
  const zh = language === "zh";
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState(query.search);

  // A click on a group row resets the search from outside.
  useEffect(() => {
    setSearchText(query.search);
  }, [query.search]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          page: String(query.page),
          page_size: String(PAGE_SIZE),
        });
        if (cycle) params.set("cycle", cycle);
        if (query.groupId) params.set("group_id", query.groupId);
        if (query.search) params.set("search", query.search);

        const token = await currentUser.getIdToken();
        const response = await fetch(
          `${getApiUrl()}/api/v2/usage-billing/admin/users?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        // A proxy error page (a 502 from Cloud Run, say) isn't JSON.
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success) {
          throw new Error(data.error || `Request failed (${response.status})`);
        }
        if (!cancelled) setResult(data);
      } catch (err) {
        console.error("Failed to load usage users:", err);
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // A slower answer to an older query must not overwrite a newer one.
    return () => {
      cancelled = true;
    };
  }, [currentUser, cycle, query.groupId, query.search, query.page, refreshKey]);

  const filtered = Boolean(query.groupId || query.search);
  const users = result?.users || [];
  const total = result?.total || 0;
  const first = total === 0 ? 0 : (query.page - 1) * PAGE_SIZE + 1;
  const last = Math.min(query.page * PAGE_SIZE, total);

  const th =
    "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider";
  const td = "px-4 py-3 text-sm text-gray-700 whitespace-nowrap";
  const columns = isCurrent ? 8 : 5;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-200 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {zh ? "使用者" : "Users"}
          </h3>
          <p className="text-sm text-gray-500">
            {zh
              ? "本週期有使用 AI 的人，依花費由高到低排列。點選使用者查看每週用量"
              : "People who used AI in this cycle, highest spend first. Click someone to see their weeks"}
            {canEdit && (zh ? "或調整額度。" : " or change their limit.")}
            {!canEdit && (zh ? "。" : ".")}
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={onPickUser}
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-orange-500 hover:bg-orange-600"
          >
            <FiUserPlus className="w-4 h-4" aria-hidden="true" />
            {zh ? "為任何使用者設定額度" : "Set a limit for any user"}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex flex-col md:flex-row gap-3">
        <form
          className="flex flex-1 gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            onQueryChange({ ...query, search: searchText.trim(), page: 1 });
          }}
        >
          <div className="relative flex-1">
            <FiSearch
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              aria-hidden="true"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={zh ? "搜尋電子郵件或姓名" : "Search email or name"}
              className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm bg-white focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-500 hover:bg-blue-600"
          >
            {zh ? "搜尋" : "Search"}
          </button>
          {query.search && (
            <button
              type="button"
              onClick={() => onQueryChange({ ...query, search: "", page: 1 })}
              className="px-3 py-2 rounded-lg text-sm text-gray-600 border border-gray-200 bg-white hover:bg-gray-50"
              aria-label={zh ? "清除搜尋" : "Clear search"}
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
        </form>
        <select
          value={query.groupId}
          onChange={(e) =>
            onQueryChange({ ...query, groupId: e.target.value, page: 1 })
          }
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
          aria-label={zh ? "依群組篩選" : "Filter by group"}
        >
          <option value="">{zh ? "所有群組" : "All groups"}</option>
          <option value="__none__">{zh ? "無群組" : "No group"}</option>
          {groups
            .filter((g) => g.group_id)
            .map((g) => (
              <option key={g.group_id} value={g.group_id}>
                {g.name}
              </option>
            ))}
        </select>
      </div>

      {error && (
        <div className="px-5 py-3 text-sm text-red-700 bg-red-50 border-b border-red-200">
          {zh ? "無法載入使用者：" : "Couldn't load users: "}
          {error}
        </div>
      )}

      <div
        className={`overflow-x-auto transition-opacity ${loading && result ? "opacity-60" : ""}`}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={th}>{zh ? "使用者" : "User"}</th>
              <th className={th}>{zh ? "群組" : "Group"}</th>
              <th className={th}>{zh ? "每月額度" : "Monthly limit"}</th>
              <th className={th}>{zh ? "本週期花費" : "Cycle spend"}</th>
              {isCurrent && (
                <>
                  <th className={th}>{zh ? "本週 / 額度" : "This week / quota"}</th>
                  <th className={th}>{zh ? "剩餘" : "Remaining"}</th>
                </>
              )}
              <th className={th}>{zh ? "提問" : "Questions"}</th>
              {isCurrent && <th className={th}>{zh ? "狀態" : "Status"}</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {!result && loading && (
              <tr>
                <td colSpan={columns} className="px-4 py-10 text-center">
                  <div className="inline-block w-6 h-6 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
                </td>
              </tr>
            )}
            {result && users.length === 0 && (
              <tr>
                <td
                  colSpan={columns}
                  className="px-4 py-10 text-center text-sm text-gray-500"
                >
                  {filtered
                    ? zh
                      ? "沒有符合條件的使用者。"
                      : "No users match."
                    : zh
                      ? "本週期還沒有人使用 AI。"
                      : "No one has used AI in this cycle yet."}
                </td>
              </tr>
            )}
            {users.map((user) => {
              const percent = percentUsed(user.week_used_usd, user.this_week_quota_usd);
              return (
                <tr
                  key={user.uid}
                  onClick={() => onSelectUser(user.uid)}
                  className="hover:bg-orange-50/50 cursor-pointer"
                >
                  <td className={td}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectUser(user.uid);
                      }}
                      className="text-left"
                    >
                      <span className="block font-medium text-gray-900 hover:text-orange-600">
                        {user.display_name || (zh ? "未命名" : "No name")}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {user.email || user.uid}
                      </span>
                    </button>
                  </td>
                  <td className={td}>
                    {user.group_name || user.group_id || (
                      <span className="text-gray-400">
                        {zh ? "無群組" : "No group"}
                      </span>
                    )}
                  </td>
                  <td className={td}>
                    {user.monthly_limit_usd === null ? (
                      <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800">
                        {zh ? "未設定額度" : "No limit"}
                      </span>
                    ) : (
                      <>
                        <span className="tabular-nums">
                          {formatMoney(user.monthly_limit_usd)}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {adminLimitSourceLabel(user.limit_source, language)}
                        </span>
                      </>
                    )}
                  </td>
                  <td
                    className={`${td} tabular-nums font-semibold`}
                    title={`Gemini ${formatMoney(
                      user.cycle_breakdown_usd.gemini_input +
                        user.cycle_breakdown_usd.gemini_output,
                    )} · GPT ${formatMoney(
                      user.cycle_breakdown_usd.gpt_input +
                        user.cycle_breakdown_usd.gpt_output,
                    )}`}
                  >
                    {formatMoney(user.cycle_used_usd)}
                  </td>
                  {isCurrent && (
                    <>
                      <td className={td}>
                        <span className="tabular-nums">
                          {formatMoney(user.week_used_usd)}
                          <span className="text-gray-400">
                            {" / "}
                            {user.this_week_quota_usd === null
                              ? zh
                                ? "無額度"
                                : "no limit"
                              : formatMoney(user.this_week_quota_usd)}
                          </span>
                        </span>
                        {percent !== null && (
                          <div className="mt-1 w-28 h-1.5 bg-orange-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                user.over_quota && enforced
                                  ? "bg-red-500"
                                  : "bg-orange-500"
                              }`}
                              style={{ width: `${Math.min(percent, 100)}%` }}
                            />
                          </div>
                        )}
                      </td>
                      <td className={`${td} tabular-nums`}>
                        {formatMoney(user.remaining_usd)}
                      </td>
                    </>
                  )}
                  <td className={`${td} tabular-nums`}>{user.cycle_turns}</td>
                  {isCurrent && (
                    <td className={td}>
                      {user.over_quota &&
                        (enforced ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                            {zh ? "已封鎖" : "Blocked"}
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                            {zh ? "已超過額度" : "Over quota"}
                          </span>
                        ))}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {result && total > 0 && (
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-3 text-sm">
          <span className="text-gray-600">
            {zh
              ? `第 ${first}–${last} 位，共 ${total} 位`
              : `${first}–${last} of ${total}`}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onQueryChange({ ...query, page: query.page - 1 })}
              disabled={query.page <= 1 || loading}
              className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {zh ? "上一頁" : "Previous"}
            </button>
            <button
              type="button"
              onClick={() => onQueryChange({ ...query, page: query.page + 1 })}
              disabled={last >= total || loading}
              className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {zh ? "下一頁" : "Next"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
